import {
  ref as rtdbRef,
  push as rtdbPush,
  get as rtdbGet,
  onValue,
  set as rtdbSet,
  update as rtdbUpdate,
  remove as rtdbRemove,
} from 'firebase/database';
import { rtdb } from './firebase';

/**
 * Pool of generic banner images the RN app falls back to on a brand detail
 * screen when that brand has no bespoke `heroImage`.
 *
 * Lives in the Realtime Database at `configuration/brandBannerFallbacks`
 * (all watchlock app-config is RTDB, not Firestore) and is nested under the
 * existing `configuration/` node so no new RTDB rule is needed — same
 * placement as `configuration/aiImageRecognition`.
 *
 * Shape:
 *   configuration/brandBannerFallbacks = {
 *     enabled: boolean,
 *     images: { [id]: BrandBannerFallbackImage },
 *   }
 *
 * `images` is a keyed map rather than an array: RTDB has no real arrays, and a
 * map means adding/removing one image is a single-key write that can't clobber
 * a concurrent edit to a sibling. There is deliberately no explicit order
 * field — the RN app picks one image per brand by hashing the brand id, so
 * pool order is meaningless to it, and the admin list just sorts by `addedAt`.
 */
const BRAND_BANNER_FALLBACKS_PATH = '/configuration/brandBannerFallbacks';
const IMAGES_PATH = `${BRAND_BANNER_FALLBACKS_PATH}/images`;

/**
 * Aspect ratio of the RN brand-detail hero slot, as width/height.
 *
 * Taken from `BrandDetailScreen`: the hero/map container spans the content
 * width (≈343pt on a 375pt phone after the 16pt side padding) at a fixed
 * height of 180pt (280pt on tablet), rendered with `resizeMode="cover"`. The
 * admin preview crops to the same ratio so what the admin approves is what
 * ships. If RN ever changes that height, change this constant.
 */
export const BANNER_ASPECT_WIDTH = 343;
export const BANNER_ASPECT_HEIGHT = 180;

/**
 * Below this the image is upscaled on a 2x/3x phone screen and looks soft.
 * Advisory only — we warn but never reject, because an admin can't fix a
 * rejected file from inside this tool.
 */
export const BANNER_MIN_RECOMMENDED_WIDTH = 686;

export interface BrandBannerFallbackImage {
  /** RTDB key. Also the basename of the Storage object. */
  id: string;
  /** Public download URL — this is what RN reads and renders directly. */
  url: string;
  /** Storage object path, kept so a hard delete can remove the bytes too. */
  storagePath: string;
  /** Original upload filename, for admin recognition only. */
  filename: string;
  /** Soft-disable. `false` keeps the row but takes it out of the RN pool. */
  enabled: boolean;
  /** Epoch ms. Sole ordering key for the admin list. */
  addedAt: number;
  /** uid of the admin who uploaded it. */
  addedBy: string;
  /** Optional caption RN may render over/under the banner. */
  label?: string;
  /**
   * Optional attribution URL. RN already renders `Image: {hostname}` from
   * `heroImageSourceUrl` on bespoke hero images; this is the pool equivalent.
   */
  creditUrl?: string;
}

export interface BrandBannerFallbacksConfig {
  enabled: boolean;
  /** Sorted oldest-first by `addedAt`. */
  images: BrandBannerFallbackImage[];
}

export const DEFAULT_BRAND_BANNER_FALLBACKS_CONFIG: BrandBannerFallbacksConfig = {
  enabled: false,
  images: [],
};

/**
 * Coerce one raw RTDB child into an image row, or `null` if it is too damaged
 * to render (no id or no URL — everything else has a safe default).
 *
 * Defensive rather than trusting: this node is hand-editable in the Firebase
 * console, and a half-written row shouldn't blank the whole panel.
 */
function normaliseImage(id: string, value: unknown): BrandBannerFallbackImage | null {
  if (!id || !value || typeof value !== 'object') return null;
  const node = value as Record<string, unknown>;

  const url = typeof node.url === 'string' ? node.url : '';
  if (!url) return null;

  const label = typeof node.label === 'string' ? node.label.trim() : '';
  const creditUrl = typeof node.creditUrl === 'string' ? node.creditUrl.trim() : '';

  return {
    id,
    url,
    storagePath: typeof node.storagePath === 'string' ? node.storagePath : '',
    filename: typeof node.filename === 'string' ? node.filename : '',
    // Absent means enabled: rows written before the flag existed, or by hand in
    // the console, should be live rather than silently invisible.
    enabled: node.enabled !== false,
    addedAt: typeof node.addedAt === 'number' ? node.addedAt : 0,
    addedBy: typeof node.addedBy === 'string' ? node.addedBy : '',
    ...(label ? { label } : {}),
    ...(creditUrl ? { creditUrl } : {}),
  };
}

/**
 * Coerce the whole node. Missing/invalid values fall back to the defaults, so
 * an absent node reads as "feature off, empty pool" rather than throwing.
 */
function normalise(value: unknown): BrandBannerFallbacksConfig {
  const node = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  const rawImages =
    node.images && typeof node.images === 'object'
      ? (node.images as Record<string, unknown>)
      : {};

  const images = Object.entries(rawImages)
    .map(([id, raw]) => normaliseImage(id, raw))
    .filter((image): image is BrandBannerFallbackImage => image !== null)
    // Oldest first. Tie-break on id so the order is stable across renders when
    // a batch upload stamps several rows in the same millisecond.
    .sort((a, b) => a.addedAt - b.addedAt || a.id.localeCompare(b.id));

  return {
    enabled: node.enabled === true,
    images,
  };
}

export async function getBrandBannerFallbacks(): Promise<BrandBannerFallbacksConfig> {
  const snapshot = await rtdbGet(rtdbRef(rtdb, BRAND_BANNER_FALLBACKS_PATH));
  return snapshot.exists()
    ? normalise(snapshot.val())
    : DEFAULT_BRAND_BANNER_FALLBACKS_CONFIG;
}

export function subscribeToBrandBannerFallbacks(
  callback: (config: BrandBannerFallbacksConfig) => void,
  onError?: (err: Error) => void,
): () => void {
  return onValue(
    rtdbRef(rtdb, BRAND_BANNER_FALLBACKS_PATH),
    (snapshot) => {
      callback(
        snapshot.exists()
          ? normalise(snapshot.val())
          : DEFAULT_BRAND_BANNER_FALLBACKS_CONFIG,
      );
    },
    (err) => {
      console.error('subscribeToBrandBannerFallbacks failed:', err);
      onError?.(err instanceof Error ? err : new Error(String(err)));
    },
  );
}

/**
 * Master on/off for the whole fallback feature. `update` on the parent node so
 * the `images` map is untouched — turning the feature off must not lose the pool.
 */
export async function setBrandBannerFallbacksEnabled(enabled: boolean): Promise<void> {
  await rtdbUpdate(rtdbRef(rtdb, BRAND_BANNER_FALLBACKS_PATH), { enabled });
}

/**
 * Mint an id for a new image before the bytes are uploaded.
 *
 * A `push` key rather than a random string: the caller needs the id up front
 * to name the Storage object, and push keys are chronologically ordered, so a
 * row whose `addedAt` failed to write still sorts sensibly. `push` with no
 * value writes nothing — it only allocates the key client-side.
 */
export function newBrandBannerFallbackId(): string {
  const key = rtdbPush(rtdbRef(rtdb, IMAGES_PATH)).key;
  if (!key) throw new Error('Could not allocate an id for the banner image');
  return key;
}

/**
 * Write one image row. `set` on the child (not `update` on the map) so the row
 * is exactly what we pass and a retry can't leave stale keys behind.
 */
export async function addBrandBannerFallback(
  image: BrandBannerFallbackImage,
): Promise<void> {
  const { id, label, creditUrl, ...rest } = image;
  await rtdbSet(rtdbRef(rtdb, `${IMAGES_PATH}/${id}`), {
    ...rest,
    // RTDB rejects `undefined`, so optional fields are omitted rather than nulled.
    ...(label ? { label } : {}),
    ...(creditUrl ? { creditUrl } : {}),
  });
}

/**
 * Patch one image row. Pass `null` for `label`/`creditUrl` to clear the key
 * (RTDB deletes a key written as null); omit them to leave them alone.
 */
export async function updateBrandBannerFallback(
  id: string,
  patch: {
    enabled?: boolean;
    label?: string | null;
    creditUrl?: string | null;
    url?: string;
    storagePath?: string;
    filename?: string;
  },
): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (patch.enabled !== undefined) payload.enabled = patch.enabled;
  if (patch.url !== undefined) payload.url = patch.url;
  if (patch.storagePath !== undefined) payload.storagePath = patch.storagePath;
  if (patch.filename !== undefined) payload.filename = patch.filename;
  if (patch.label !== undefined) payload.label = patch.label || null;
  if (patch.creditUrl !== undefined) payload.creditUrl = patch.creditUrl || null;

  if (Object.keys(payload).length === 0) return;
  await rtdbUpdate(rtdbRef(rtdb, `${IMAGES_PATH}/${id}`), payload);
}

/** Drop one image row. The Storage object is deleted separately, best effort. */
export async function removeBrandBannerFallback(id: string): Promise<void> {
  await rtdbRemove(rtdbRef(rtdb, `${IMAGES_PATH}/${id}`));
}
